import React, { MutableRefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import find from 'lodash/find'
import { muiTheme } from 'theme'
import { Box, Chip, Grid, IconButton, InputAdornment, OutlinedInput, Popover } from '@material-ui/core'
import SearchIcon from '@material-ui/icons/Search'
import { ThemeProvider } from '@material-ui/styles'
import PopupState, { bindPopover, bindTrigger } from 'material-ui-popup-state'
import MoreVertIcon from '@material-ui/icons/MoreVert'
import ClearIcon from '@material-ui/icons/Clear'
// @ts-ignore
import { ANCHOR_LEFT, ANCHOR_RIGHT } from 'react-dates/constants'
import { Column, Row, TableInstance } from 'react-table'
import * as H from 'history'
import SummaryCards, { SummaryCard } from 'civic-champs-shared/core/SummaryCards'
import NewFilters from 'core/table/components/NewFilters'
import PagedTable from 'core/table/components/PagedTable'
import { ColumnSelector } from 'core/table/components/ColumnSelector'
import { ColumnState } from 'core/table/interfaces/ColumnState'
import useExtendedTableStyles from 'core/table/table-hooks/useExtendedTableStyles'
import useSetFilterRowsHistory from 'core/table/table-hooks/useSetFiltersRowHistory'
import useTablePersistence from 'core/table/table-hooks/useTablePersistence'
import { BinaryFilter, FilterRow, GetOperatorOptionsType } from 'core/table/interfaces/Filters'
import { formatDateFilterOperandForDisplay, isDateFilterOperator } from 'core/table/dateFilterUtils'
import DateRangeFilter from 'components/filter/StyledDateRangeFilter'
import { useDateRangeFilter } from 'filtering/hooks'
import useDebounce from 'utils/useDebounce'
import { mergeColumnOrder } from 'core/table/utils'
import { getColumnHeaderText } from 'core/table/utils'
import map from 'lodash/map'
import filter from 'lodash/filter'
import isUndefined from 'lodash/isUndefined'
import cn from 'classnames'
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp'
import moment from 'moment'
import { useTranslation } from 'react-i18next'
import { useInternationalizationEnabled } from 'i18n/feature'
import { useFiltersFromQuery } from 'core/table/components/NewFiltersModal'
import { handleNavigateToLastAddedRow } from 'core/table/table-hooks/useNavigateToLastAddedRow'
import { EventListener } from 'civic-champs-shared/api/hooks/useRemoteCollection'

interface Summary {
  [key: string]: any
}

const getOperandTextValue = (operand: any): string => {
  if (operand?.label) {
    return operand.label
  } else if (Array.isArray(operand)) {
    return operand.map(getOperandTextValue).join(', ')
  }
  return operand as string
}

export interface ExtendedPagedTableProps<
  T extends object = {},
  I extends { id: string | number } = { id: string | number },
> {
  summary?: Summary
  getSummary?: (rows: Row<T>[]) => Summary
  cards?: SummaryCard[]
  useGlobalSearch?: boolean
  useFilters?: boolean
  useDateRange?: boolean
  history?: H.History
  location?: H.Location
  table: TableInstance<any>
  columns: any[]
  getOperatorOptions?: GetOperatorOptionsType
  binaryFilters?: BinaryFilter[]
  filterSubHeader?: string
  filterButton?: JSX.Element
  buttons?: JSX.Element
  addButton?: JSX.Element
  columnState?: ColumnState[]
  popover?: JSX.Element
  popoverClassName?: string
  tableName?: string
  searchPlaceholder?: string
  externalSearch?: string
  getCellStyle?: Function
  getHeaderCellStyle?: Function
  compactView?: boolean
  title?: string
  expandable?: boolean
  customDateKey?: string
  defaultDateRange?: {
    startDate: moment.Moment
    endDate: moment.Moment
  }
  collectionKey?: string
  eventListeners?: {
    onAdd: EventListener<T>
    onRemove: EventListener<T>
    onUpdate: EventListener<T>
    onSync: EventListener<T>
    onBeforeUpdate: EventListener<T>
    onBeforeAdd: EventListener<T>
    onBeforeRemove: EventListener<T>
    onBeforeSync: EventListener<T>
    onIsNewInCollectionRemoved: EventListener<T>
  }
  skipReset?: MutableRefObject<boolean | undefined>
  stackedSectionHeader?: boolean
  useColumnSelector?: boolean
  serverSide?: boolean
  totalCount?: number
  onPageChange?: (pageIndex: number) => void
  onPageSizeChange?: (pageSize: number) => void
  onSearchChange?: (query: string) => void
  onFiltersChange?: () => void
  pageIndex?: number
}

export function ExtendedPagedTableComponent<
  T extends object = {},
  I extends { id: string | number } = { id: string | number },
>(props: ExtendedPagedTableProps<T, I>) {
  const {
    cards,
    useGlobalSearch,
    useFilters,
    useDateRange,
    history,
    table,
    columns,
    getOperatorOptions,
    filterButton,
    buttons,
    popover,
    popoverClassName,
    addButton,
    binaryFilters,
    columnState = null,
    tableName,
    searchPlaceholder: searchPlaceholderProp,
    filterSubHeader,
    getCellStyle,
    getHeaderCellStyle,
    externalSearch,
    compactView = false,
    title,
    customDateKey,
    defaultDateRange,
    expandable = true,
    stackedSectionHeader = false,
    skipReset,
    useColumnSelector = true,
    collectionKey,
    serverSide = false,
    totalCount,
    onPageChange,
    onPageSizeChange,
    onSearchChange,
    onFiltersChange,
    pageIndex: controlledPageIndex,
    eventListeners: {
      onAdd,
      onUpdate,
      onBeforeAdd,
      onBeforeUpdate,
      onBeforeSync,
      onBeforeRemove,
      onIsNewInCollectionRemoved,
    } = {},
  } = props
  const { t } = useTranslation('admin')
  const internationalizationEnabled = useInternationalizationEnabled()
  const searchPlaceholder = searchPlaceholderProp ?? (internationalizationEnabled ? t('table.search') : 'Search')
  const classes = useExtendedTableStyles({ compactView })
  const [dateRange, setDateRange] = useDateRangeFilter({ key: customDateKey, defaultDateRange })
  const { filterRows: initialFilterRows } = useFiltersFromQuery(history?.location?.search || '', getOperatorOptions)
  const hasUrlFilters = initialFilterRows.length > 0
  const [persistedFilterRows, setPersistedFilterRows] = useState<FilterRow[]>([])
  const [search, setSearch] = useState<string>('')
  const [expanded, setExpanded] = useState<boolean>(true)
  const debouncedSearch = useDebounce(search, 250)
  const lastReportedSearch = useRef<string | undefined>()
  const summary = useMemo(
    () => props.getSummary?.(table.flatRows) || props.summary,
    [props.summary, props.getSummary, table.flatRows],
  )
  const filterRows = useMemo(
    () => history?.location?.state?.filterRows || (hasUrlFilters ? initialFilterRows : persistedFilterRows),
    [hasUrlFilters, history, initialFilterRows, persistedFilterRows],
  )
  const setFilterRowsHistory = useSetFilterRowsHistory(history)
  const persistedFiltersSyncedToUrl = useRef(false)
  const { persistFilters } = useTablePersistence({
    tableName,
    table,
    enabled: !!tableName && !!useFilters,
    hasUrlFilters,
    onPersistedFiltersLoaded: setPersistedFilterRows,
    persistSort: !serverSide,
    serverSide,
  })

  useEffect(() => {
    if (!serverSide || !useFilters || hasUrlFilters || persistedFiltersSyncedToUrl.current) {
      return
    }
    if (!persistedFilterRows.length) {
      return
    }
    persistedFiltersSyncedToUrl.current = true
    setFilterRowsHistory(persistedFilterRows)
    onFiltersChange?.()
  }, [hasUrlFilters, onFiltersChange, persistedFilterRows, serverSide, setFilterRowsHistory, useFilters])
  useEffect(() => {
    if (serverSide) {
      const nextSearch = isUndefined(externalSearch) ? debouncedSearch : externalSearch || ''
      if (lastReportedSearch.current === nextSearch) {
        return
      }
      lastReportedSearch.current = nextSearch
      onSearchChange?.(nextSearch)
      return
    }
    // @ts-ignore
    table.setGlobalFilter && table.setGlobalFilter(isUndefined(externalSearch) ? debouncedSearch : externalSearch)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, externalSearch, onSearchChange, serverSide])

  useEffect(() => {
    if (!serverSide) {
      return
    }
    // @ts-ignore
    table.setGlobalFilter && table.setGlobalFilter('')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [table.data, serverSide])

  useEffect(() => {
    onAdd?.(handleNavigateToLastAddedRow({ table, skipReset, key: collectionKey }))
    onUpdate?.(handleNavigateToLastAddedRow({ table, skipReset, key: collectionKey }))
    onBeforeAdd?.(() => {
      skipReset && (skipReset.current = true)
    })
    onBeforeUpdate?.(() => {
      skipReset && (skipReset.current = true)
    })
    onBeforeSync?.(() => {
      skipReset && (skipReset.current = true)
    })
    onBeforeRemove?.(() => {
      skipReset && (skipReset.current = true)
    })
    onIsNewInCollectionRemoved?.(() => {
      skipReset && (skipReset.current = false)
    })
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const removeFilter = useCallback(
    (columnName: string, operator: any) => {
      const rows = filterRows.filter(
        ({ column, operator: columnOperator }: { column: string; operator: any }) =>
          column !== columnName || columnOperator !== operator,
      )
      setFilterRowsHistory(rows)
      setPersistedFilterRows(rows)
      persistFilters(rows)
      if (!serverSide) {
        // @ts-ignore
        table.setAllFilters(rows.map(({ column, value }) => ({ id: column, value })))
      } else {
        onFiltersChange?.()
      }
    },
    [filterRows, onFiltersChange, persistFilters, serverSide, setFilterRowsHistory, table],
  )

  useEffect(() => {
    if (!Array.isArray(columnState) || !columnState.length) {
      return
    }

    const allColumnIds = map(columns, 'id').filter((id): id is string => Boolean(id))
    const columnsInState = map(columnState, 'id')
    const hiddenColumnsNotInState = filter(table.initialState?.hiddenColumns || [], id => !columnsInState.includes(id))
    const savedOrder = columnState.map(({ id }) => id).filter(id => allColumnIds.includes(id))

    table.setHiddenColumns([
      ...columnState.filter(({ visible }) => !visible).map(({ id }) => id),
      ...hiddenColumnsNotInState,
    ])
    // @ts-ignore
    table.setColumnOrder(mergeColumnOrder(allColumnIds, savedOrder))
  }, [columnState, columns, table])

  const DateRange = (
    <DateRangeFilter
      initialValues={dateRange}
      onFilterApplied={setDateRange}
      anchorDirection={compactView ? ANCHOR_RIGHT : ANCHOR_LEFT}
      noBorder
    />
  )

  const showSearch = useGlobalSearch && isUndefined(externalSearch)
  const showSection = showSearch || useFilters || (compactView && useDateRange) || title || addButton

  const titleBlock = title ? (
    <div
      className={cn(classes.title, { [classes.pointer]: expandable })}
      onClick={expandable ? () => setExpanded(value => !value) : undefined}
    >
      {title} {expandable && (expanded ? <KeyboardArrowDownIcon /> : <KeyboardArrowUpIcon />)}
    </div>
  ) : null

  const searchInput = showSearch ? (
    <OutlinedInput
      labelWidth={0}
      id="search"
      placeholder={searchPlaceholder}
      value={search}
      onChange={(event: any) => setSearch(event.target.value)}
      classes={{
        root: cn(classes.searchField, stackedSectionHeader && classes.stackedSearchWrapper),
        notchedOutline: classes.notchedOutline,
        input: cn(classes.input, stackedSectionHeader && classes.stackedSearchInput),
      }}
      endAdornment={
        <InputAdornment position="end">
          <SearchIcon style={{ fontSize: '16px' }} />
        </InputAdornment>
      }
    />
  ) : null

  const filtersBlock =
    useFilters &&
    (filterButton
      ? filterButton
      : getOperatorOptions && (
          <div>
            <NewFilters
              columns={columns}
              table={table}
              filterRows={filterRows}
              history={history}
              getOperatorOptions={getOperatorOptions}
              binaryFilters={binaryFilters}
              subHeader={filterSubHeader}
              serverSide={serverSide}
              onFiltersChange={onFiltersChange}
              onPersistFilters={rows => {
                setPersistedFilterRows(rows)
                persistFilters(rows)
              }}
            />
          </div>
        ))

  return (
    <div className={classes.container}>
      <div className={classes.topContainer}>
        {cards && summary && (
          <div className={classes.summary}>
            <SummaryCards cards={cards} data={summary} />
          </div>
        )}
        {showSection &&
          (stackedSectionHeader ? (
            <div className={classes.stackedSectionContainer}>
              <div className={classes.sectionHeaderRow}>
                <div className={classes.sectionHeaderLeft}>
                  {titleBlock}
                  {compactView && useDateRange && <Grid item>{DateRange}</Grid>}
                </div>
                {addButton && <div>{addButton}</div>}
              </div>
              {(showSearch || useFilters) && (
                <div className={classes.searchRow}>
                  {searchInput}
                  {filtersBlock}
                </div>
              )}
            </div>
          ) : (
            <div className={classes.sectionContainer}>
              {(showSearch || useFilters) && (
                <div className={cn(classes.buttonsContainer, { [classes.headerCompactView]: compactView })}>
                  {titleBlock}
                  {searchInput && <div>{searchInput}</div>}
                  {filtersBlock}
                  {compactView && useDateRange && <Grid item>{DateRange}</Grid>}
                </div>
              )}
              {((!compactView && useDateRange) || addButton) && (
                <div className={classes.buttons}>
                  {addButton && <div>{addButton}</div>}
                  {!compactView && useDateRange && <div>{DateRange}</div>}
                </div>
              )}
            </div>
          ))}
        {useFilters && !!filterRows.length && (
          <div className={classes.buttonsContainer}>
            {filterRows
              // @ts-ignore
              ?.map(({ column, operator: rowOperator, value: { operator, operand } }, index) => {
                const filterOperator = operator || rowOperator
                const columnInstance = find(table.columns, { id: column })
                operand =
                  (columnInstance as any)?.getOperandTextValue?.(operand, filterOperator) ||
                  (isDateFilterOperator(filterOperator)
                    ? formatDateFilterOperandForDisplay(filterOperator, operand)
                    : getOperandTextValue(operand))
                if (typeof operand === 'string' && operand.length > 50) {
                  operand = operand.substr(0, 47) + '...'
                }
                const operatorLabel = getOperatorOptions?.(column)?.find(
                  option => option.value === filterOperator,
                )?.displayValue
                const label = operand
                  ? /^[><]=?$/.test(filterOperator)
                    ? `${filterOperator} "${operand}"`
                    : operand
                  : operatorLabel || filterOperator
                return (
                  <div key={index}>
                    <Chip
                      className={classes.filterChip}
                      label={`${getColumnHeaderText(columnInstance)}: ${label}`}
                      onDelete={() => removeFilter(column, operator)}
                      color="secondary"
                      deleteIcon={<ClearIcon className={classes.filterChipIcon} />}
                    />
                  </div>
                )
              })}
          </div>
        )}
      </div>
      <div className={classes.tableContainer}>
        <Grid
          item
          xs={12}
          // To animate with css transition, we need to set a max-height, average row height is 43px 200px is for the rest
          style={
            expandable && compactView ? { maxHeight: expanded ? (table as any).page.length * 43 + 200 + 'px' : 0 } : {}
          }
          className={classes.wrapper}
        >
          <PagedTable
            compactView={compactView}
            {...table}
            // @ts-ignore
            totalCount={serverSide ? totalCount : undefined}
            // @ts-ignore
            controlledPageIndex={serverSide ? controlledPageIndex : undefined}
            // @ts-ignore
            onPageChange={serverSide ? onPageChange : undefined}
            // @ts-ignore
            onPageSizeChange={serverSide ? onPageSizeChange : undefined}
            // @ts-ignore
            header={
              <Grid item container xs={12} justify="space-between" className={classes.header}>
                <Grid item>
                  {buttons}
                  {popover && (
                    <PopupState variant="popover" popupId="menu-buttons-popover">
                      {popupState => (
                        <>
                          <IconButton
                            className={classes.popoverButton}
                            aria-label="show more"
                            component="span"
                            {...bindTrigger(popupState)}
                          >
                            <MoreVertIcon />
                          </IconButton>
                          <Popover
                            {...bindPopover(popupState)}
                            anchorOrigin={{
                              vertical: 'bottom',
                              horizontal: 'left',
                            }}
                            transformOrigin={{
                              vertical: 'top',
                              horizontal: 'left',
                            }}
                          >
                            <Box className={cn(classes.popover, popoverClassName)} p={2}>
                              {popover}
                            </Box>
                          </Popover>
                        </>
                      )}
                    </PopupState>
                  )}
                </Grid>
                {useColumnSelector && (
                  <Grid item>
                    <ColumnSelector table={table} tableName={tableName} />
                  </Grid>
                )}
              </Grid>
            }
            // @ts-ignore
            newStyles
            isDraggable
            getCellStyle={getCellStyle}
            getHeaderCellStyle={getHeaderCellStyle}
          />
        </Grid>
      </div>
    </div>
  )
}

export function ExtendedPagedTable<T extends object = {}, I extends { id: string | number } = { id: string | number }>(
  props: ExtendedPagedTableProps<T, I>,
) {
  return (
    <ThemeProvider theme={muiTheme}>
      <ExtendedPagedTableComponent<T> {...props} />
    </ThemeProvider>
  )
}
